<template>
  <div>
    <section class="relative isolate overflow-hidden pt-24 pb-12 sm:pt-32 sm:pb-16">
      <shape-field />

      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div
          data-animate-group
          class="mx-auto max-w-3xl text-center"
        >
          <atomic-eyebrow label="Fourniture, pose, installation" />

          <h1 class="mt-6 text-balance text-5xl font-display font-bold tracking-tight text-gray-900 sm:text-6xl">
            Écrans professionnels d'affichage dynamique
          </h1>

          <p
            data-animate="fade-up"
            class="mt-6 text-pretty text-lg/8 text-gray-600"
          >
            De l'écran vitrine haute luminosité au mur d'écrans LED, de la borne tactile au totem vidéo&nbsp;: nous équipons vos espaces partout en Suisse, avec notre partenaire
            <nuxt-link
              to="#beecolor"
              class="font-semibold text-brand-600 bg-gradient-to-r from-current to-current bg-left-bottom bg-no-repeat bg-[length:0%_1px] transition-[background-size,color] duration-200 ease-out hover:bg-[length:100%_1px] hover:text-brand-700 active:text-brand-700 motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
            >
              {{ beecolor.name }}
            </nuxt-link>
            pour la pose et l'installation. Le tout géré à distance depuis le logiciel Live Display.
          </p>

          <div
            data-animate="fade-up"
            class="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button-primary
              link="/devis"
              label="Demander un devis"
              size="lg"
            />
            <button-secondary
              link="/box"
              label="Voir la box Live Display"
              size="lg"
              icon-name="ph:arrow-right"
            />
          </div>
        </div>
      </div>
    </section>

    <section class="pt-12 pb-24 sm:pt-16 sm:pb-32">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div
          data-animate-group
          class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-6"
        >
          <div
            v-for="category in screenCategories"
            :key="category.title"
            data-animate="fade-up"
            :class="getCategoryClass(category.isHighlighted)"
            class="flex flex-col gap-y-4 rounded-3xl p-8"
          >
            <atomic-icon-square
              :icon-name="category.iconName"
              :size="category.isHighlighted ? 'lg' : 'md'"
            />

            <h3
              :class="category.isHighlighted ? 'text-2xl' : 'text-lg'"
              class="font-display font-semibold tracking-tight text-gray-900"
            >
              {{ category.title }}
            </h3>

            <p
              :class="category.isHighlighted ? 'text-base/7' : 'text-sm/6'"
              class="text-gray-600"
            >
              {{ category.description }}
            </p>

            <atomic-check-list :items="category.strengths" class="mt-auto pt-2" />
          </div>
        </div>
      </div>
    </section>

    <section class="relative isolate overflow-hidden py-24 sm:py-32">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <atomic-section-heading
          align="center"
          kicker="Solution clés en main"
          title="Logiciel d'affichage dynamique et écrans réunis"
          description="Un seul interlocuteur pour vos écrans professionnels, leur installation et leur gestion. De l'écran vitrine à la signalétique numérique, vos contenus sont mis à jour à distance et en temps réel depuis le logiciel Live Display."
          class="mx-auto"
        />

        <div
          data-animate-group
          class="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6"
        >
          <div
            v-for="step in solutionSteps"
            :key="step.title"
            data-animate="fade-up"
            class="flex flex-col gap-y-4 rounded-3xl bg-white ring-1 ring-gray-200 shadow-soft p-8"
          >
            <atomic-icon-square
              :icon-name="step.iconName"
            />

            <h3 class="text-xl font-display font-semibold tracking-tight text-gray-900">
              {{ step.title }}
            </h3>

            <p class="text-base/7 text-gray-600">
              {{ step.description }}
            </p>
          </div>
        </div>

        <div
          data-animate="fade-up"
          class="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button-primary
            outline
            link="/fonctionnalites"
            label="Voir les fonctionnalités du logiciel"
            icon-name="ph:arrow-right"
          />
        </div>
      </div>
    </section>

    <section class="relative isolate overflow-hidden py-24 sm:py-32">
      <shape-field origin="right" />

      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <atomic-section-heading
          align="center"
          kicker="Partenaire suisse"
          :title="`Signalétique et communication visuelle par ${beecolor.name}`"
          class="mx-auto"
        >
          <template #description>
            {{ beecolor.description }} Au-delà de la pose de vos écrans,
            <nuxt-link
              to="#beecolor"
              class="font-semibold text-brand-600 bg-gradient-to-r from-current to-current bg-left-bottom bg-no-repeat bg-[length:0%_1px] transition-[background-size,color] duration-200 ease-out hover:bg-[length:100%_1px] hover:text-brand-700 active:text-brand-700 motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
            >
              {{ beecolor.name }}
            </nuxt-link>
            réalise l'ensemble de votre identité visuelle, en intérieur comme en extérieur.
          </template>
        </atomic-section-heading>

        <div
          data-animate-group
          class="mt-16 grid grid-cols-1 gap-x-12 gap-y-12 lg:grid-cols-2"
        >
          <div
            v-for="service in signageServices"
            :key="service.title"
            data-animate="fade-up"
            class="flex gap-x-5 sm:gap-x-6"
          >
            <atomic-icon-square
              :icon-name="service.iconName"
              size="lg"
            />

            <div>
              <h3 class="text-xl font-display font-semibold tracking-tight text-gray-900">
                {{ service.title }}
              </h3>

              <p class="mt-3 text-base/7 text-gray-600">
                {{ service.description }}
              </p>

              <ul role="list" class="mt-5 flex flex-wrap gap-2">
                <li
                  v-for="item in service.items"
                  :key="item"
                >
                  <atomic-badge :label="item" />
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div
          id="beecolor"
          data-animate="zoom"
          class="mt-20 scroll-mt-24 overflow-hidden rounded-3xl bg-white ring-1 ring-gray-200 shadow-soft"
        >
          <div class="p-8 sm:p-12">
            <div class="flex flex-col gap-y-8 lg:flex-row lg:items-center lg:justify-between lg:gap-x-12">
              <div class="flex items-center gap-x-5">
                <nuxt-img
                  :alt="'Logo ' + beecolor.legalName"
                  :src="beecolor.logoUrl"
                  loading="lazy"
                  sizes="xs:160px sm:240px"
                  class="h-14 w-auto sm:h-16"
                />

                <div>
                  <p class="text-2xl sm:text-3xl font-display font-bold tracking-tight text-gray-900">
                    {{ beecolor.legalName }}
                  </p>

                  <p class="mt-1 text-sm text-gray-500">
                    Installation d'écrans et signalétique, partout en Suisse
                  </p>
                </div>
              </div>

              <button-primary
                outline
                :link="beecolor.url"
                target="_blank"
                rel="noopener"
                :label="`Visiter le site de ${beecolor.name}`"
                icon-name="ph:arrow-up-right"
                class="shrink-0 self-start lg:self-auto"
              />
            </div>

            <h3 class="mt-10 text-balance text-2xl sm:text-3xl font-display font-bold tracking-tight text-gray-900">
              Fourniture, pose et maintenance partout en Suisse
            </h3>

            <p class="mt-4 max-w-3xl text-lg/8 text-gray-600">
              {{ beecolor.name }} intervient sur toute la Suisse pour la fourniture, la pose et la fixation de vos écrans, ainsi que pour leur maintenance. Nous planifions et coordonnons l'intervention, et restons votre interlocuteur unique du devis au service après-vente.
            </p>
          </div>

          <dl class="grid grid-cols-1 divide-y divide-gray-100 border-t border-gray-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
            <div
              v-for="highlight in partnerHighlights"
              :key="highlight.title"
              class="p-8 sm:p-10"
            >
              <dt class="font-display font-semibold tracking-tight text-gray-900">
                {{ highlight.title }}
              </dt>

              <dd class="mt-2 text-sm/6 text-gray-600">
                {{ highlight.description }}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </section>

    <section-faq
      :items="faq"
      kicker="Achat, location, installation"
      title="Questions fréquentes sur les écrans professionnels"
      description="Choix du type d'écran, achat ou location, prise en charge de l'installation et compatibilité avec le logiciel."
      footer-text="Un projet d'écrans professionnels&nbsp;?"
      footer-label="Demander un devis"
      footer-link="/devis"
    />

    <section-cta
      title="Prêt à équiper vos espaces&nbsp;?"
      description="Décrivez-nous votre projet&nbsp;: nous vous conseillons sur les écrans adaptés, puis nous nous occupons de la fourniture, de la pose et de la mise en service."
      primary-label="Demander un devis"
      primary-link="/devis"
      secondary-label="Nous contacter"
      secondary-link="/contact"
    />

    <section-related-links :links="relatedLinks" />
  </div>
</template>

<script setup lang="ts">
type ScreenCategory = {
  iconName: string,
  title: string,
  description: string,
  strengths: string[],
  isHighlighted: boolean
}

type SignageService = {
  iconName: string,
  title: string,
  description: string,
  items: string[]
}

type SolutionStep = {
  iconName: string,
  title: string,
  description: string
}

type PartnerHighlight = {
  title: string,
  description: string
}

const beecolor = partnerBeecolor

const screenCategories: ScreenCategory[] = [
  {
    iconName: 'ph:squares-four-duotone',
    title: 'Murs d\'écrans & panneaux LED grand format',
    description: 'Des vidéo-walls et panneaux LED spectaculaires pour vos halls d\'accueil, centres commerciaux et vitrines. Un affichage grand format qui capte le regard à distance.',
    strengths: [
      'Impact visuel maximal sur les grands espaces',
      'Assemblage sur mesure selon vos dimensions',
      'Luminosité adaptée à l\'intérieur comme à l\'extérieur'
    ],
    isHighlighted: true
  },
  {
    iconName: 'ph:sun-duotone',
    title: 'Écrans vitrine haute luminosité',
    description: 'Des écrans anti-reflets à haute luminosité, lisibles en plein soleil derrière votre vitrine. Idéaux pour les commerces et agences en front de rue.',
    strengths: [
      'Parfaitement lisibles en plein jour',
      'Technologie haute nit anti-reflets',
      'Écrans suspendus ou sur pied disponibles'
    ],
    isHighlighted: true
  },
  {
    iconName: 'ph:hand-tap-duotone',
    title: 'Bornes tactiles & écrans interactifs',
    description: 'Bornes de commande, totems tactiles et kakemonos digitaux pour laisser vos visiteurs interagir\u00A0: menus, catalogues, orientation ou prise de commande en autonomie.',
    strengths: [
      'Bornes de commande et totems tactiles',
      'Expérience interactive pour vos clients',
      'Formats de sol ou muraux'
    ],
    isHighlighted: false
  },
  {
    iconName: 'ph:align-bottom-simple-duotone',
    title: 'Totems vidéo & displays dynamiques',
    description: 'Des totems vidéo élégants pour l\'intérieur et des bornes extérieures étanches, résistantes aux intempéries, pour communiquer 24h/24 en toute autonomie.',
    strengths: [
      'Totems intérieurs au design soigné',
      'Bornes extérieures étanches et robustes',
      'Résistance aux intempéries et au vandalisme'
    ],
    isHighlighted: false
  },
  {
    iconName: 'ph:monitor-duotone',
    title: 'Moniteurs professionnels 24h/24',
    description: 'Des moniteurs professionnels et Smart TV pro conçus pour fonctionner en continu, plus lumineux et plus robustes qu\'un téléviseur grand public. Pour salles de réunion, entreprises et commerces.',
    strengths: [
      'Dalles renforcées pour un usage intensif',
      'Garantie professionnelle étendue',
      'Idéaux pour l\'accueil et les salles de réunion'
    ],
    isHighlighted: false
  }
]

function getCategoryClass (isHighlighted: boolean): string {
  if (isHighlighted) {
    return 'lg:col-span-3 bg-white ring-1 ring-gray-200 shadow-soft'
  }
  return 'lg:col-span-2 bg-white ring-1 ring-gray-200 shadow-soft'
}

const signageServices: SignageService[] = [
  {
    iconName: 'ph:storefront-duotone',
    title: 'Enseignes & habillage de vitrines',
    description: 'Habillage complet de vos façades et vitrines, de l\'enseigne lumineuse au film adhésif.',
    items: [
      'Enseignes lumineuses et caissons',
      'Lettrage adhésif sur mesure',
      'Vitrophanie et films dépolis',
      'Films solaires et anti-regards'
    ]
  },
  {
    iconName: 'ph:printer-duotone',
    title: 'Impression grand format & panneaux',
    description: 'Impression grand format sur supports rigides ou souples, pour l\'intérieur comme l\'extérieur.',
    items: [
      'Panneaux Dibond® et Forex®',
      'Plexiglas® et supports rigides',
      'Matériaux écologiques Reboard®',
      'Bâches et supports souples'
    ]
  },
  {
    iconName: 'ph:van-duotone',
    title: 'Marquage publicitaire de véhicules',
    description: 'Transformez votre flotte en support publicitaire mobile, du lettrage simple au covering intégral.',
    items: [
      'Covering total ou partiel',
      'Lettrage de flottes utilitaires',
      'Marquage de véhicules de société',
      'Pose professionnelle en atelier'
    ]
  },
  {
    iconName: 'ph:signpost-duotone',
    title: 'PLV, stands & signalétique d\'orientation',
    description: 'Supports de communication pour vos points de vente, salons professionnels et espaces d\'accueil.',
    items: [
      'Roll-ups et présentoirs sur mesure',
      'Stands d\'exposition',
      'Signalétique d\'orientation',
      'Plaques et totems d\'accueil'
    ]
  }
]

const partnerHighlights: PartnerHighlight[] = [
  {
    title: 'Un seul interlocuteur',
    description: 'Live Display coordonne le projet et reste votre contact unique, du devis au service après-vente.'
  },
  {
    title: 'Pose professionnelle',
    description: 'Fixation, raccordement et mise en service réalisés sur site par les équipes Beecolor.'
  },
  {
    title: 'Maintenance réactive',
    description: 'Entretien des écrans et service après-vente assurés partout en Suisse.'
  }
]

const solutionSteps: SolutionStep[] = [
  {
    iconName: 'ph:chats-circle-duotone',
    title: 'On vous conseille',
    description: 'Nous étudions votre lieu et votre objectif, puis recommandons les écrans adaptés à votre activité.'
  },
  {
    iconName: 'ph:wrench-duotone',
    title: 'On installe',
    description: 'Fourniture, pose et fixation de vos écrans et mise en service, partout en Suisse.'
  },
  {
    iconName: 'ph:monitor-play-duotone',
    title: 'Vous diffusez',
    description: 'Vos écrans sont gérés à distance et mis à jour en temps réel depuis le logiciel Live Display, en quelques clics.'
  }
]

const faq = faqScreensItems

const config = useRuntimeConfig()
const env = config.public
const baseUrl = env.PUBLIC_URL.replace(/\/+$/, '')

const screenTypeNames = screenCategories.map(category => category.title)

const relatedLinks = getCrossLinks(['casUsages', 'tarifs', 'temoignages'])

useSeo({
  title: `Écrans professionnels d'affichage dynamique en Suisse — ${appName}`,
  description: 'Écrans professionnels, murs d\'écrans LED, bornes tactiles et totems vidéo en Suisse pour votre signalétique numérique : conseil, fourniture, pose et installation.',
  path: '/ecrans',
  jsonLd: [
    getBreadcrumbJsonLd(baseUrl, [
      { name: 'Accueil', path: '/' },
      { name: 'Écrans professionnels', path: '/ecrans' }
    ]),
    getLocalBusinessJsonLd(baseUrl),
    getScreensServiceJsonLd(baseUrl, screenTypeNames),
    getFaqPageJsonLd(faq)
  ]
})
</script>
